import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    BimContractSubObjectCreateInput,
    BimContractSubObjectUpdateInput,
    BimWorkOrderObjectSelectionData,
} from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { ItemTableContractObject } from 'pages/Project/BimProject/Contract/BimContractStructure/hooks/useBimItemsContractStructureTable';
import { useBimUniqueContractSubObject } from 'pages/Project/BimProject/Contract/BimContractStructure/hooks/useBimUniqueContractSubObject';
import { cnMixSpace, IconAlert, Informer } from 'uikit';
import { requiredBaseRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useBimCreateWorkOrderChangeSubObject } from '../../WorkOrderChange/bimWorkOrderChangeStructure/BimWorkOrderSubObjectChangeEdit/useBimCreateWorkOrderSubChangeObject';
import { useBimUpdateWorkOrderChangeSubObject } from '../../WorkOrderChange/bimWorkOrderChangeStructure/BimWorkOrderSubObjectChangeEdit/useBimUpdateWorkOrderChangeSubObject';

import { useBimCreateWorkOrderSubObject } from './hooks/useBimCreateWorkOrderSubObject';
import { useBimUpdateWorkOrderSubObject } from './hooks/useBimUpdateWorkOrderSubObject';

import Style from './BimWorkOrderSubObjectEdit.module.css';

interface IBimWorkOrderSubObjectEdit {
    workOrderId?: number;
    workOrderChangeId?: number;
    onClose: (isSuccess?: boolean, newSubObjectId?: number) => void;
    parentObjectToAppend?: BimWorkOrderObjectSelectionData;
    objectToEdit?: BimWorkOrderObjectSelectionData;
    isNameUnconditionallyEnabled?: boolean;
}

export const BimWorkOrderSubObjectEdit: React.FC<IBimWorkOrderSubObjectEdit> = (props) => {
    const {
        workOrderId,
        workOrderChangeId,
        onClose,
        objectToEdit,
        parentObjectToAppend,
        isNameUnconditionallyEnabled,
    } = props;

    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const { control, formState, handleSubmit, watch, setError, clearErrors } = useForm({
        defaultValues: objectToEdit,
    });

    const nameValue = watch('name');
    const rationaleValue = watch('rationale');
    const isDirtyObjectName = formState?.dirtyFields?.name;

    const { bimUniqueContractSubObjectData, bimUniqueContractSubObjectStatus, resetBimUniqueContractSubObjectName } =
        useBimUniqueContractSubObject({
            contractObject: objectToEdit as unknown as ItemTableContractObject,
            parentContractObjectId: parentObjectToAppend?.id,
            name: nameValue,
            isDirtyObjectName,
        });

    const btnConfirmTitle = useMemo(() => `${objectToEdit ? 'Редактирование' : 'Создание'} подобъекта`, [objectToEdit]);
    const btnConfirmLabel = useMemo(() => (objectToEdit ? 'Сохранить' : 'Создать'), [objectToEdit]);

    const btnConfirmDisabled = useMemo(
        () =>
            !formState?.isDirty ||
            !nameValue ||
            (isDirtyObjectName && !bimUniqueContractSubObjectData?.result) ||
            !rationaleValue,
        [bimUniqueContractSubObjectData?.result, formState?.isDirty, isDirtyObjectName, nameValue, rationaleValue],
    );

    const onSuccess = useCallback(
        (newSubObjectId?: number) => {
            onClose(true, newSubObjectId);
        },
        [onClose],
    );

    const { bimCreateWorkOrderSubObjectMakeRequest } = useBimCreateWorkOrderSubObject(workOrderId, onSuccess);

    const { bimUpdateWorkOrderSubObjectMakeRequest } = useBimUpdateWorkOrderSubObject(workOrderId, onSuccess);

    const {
        bimCreateWorkOrderChangeSubObjectData,
        bimCreateWorkOrderChangeSubObjectStatus,
        bimCreateWorkOrderChangeSubObjectError,
        bimCreateWorkOrderChangeSubObjectChangeMakeRequest,
    } = useBimCreateWorkOrderChangeSubObject(workOrderChangeId, onSuccess);

    const { bimUpdateWorkOrderSubObjectChangeMakeRequest } = useBimUpdateWorkOrderChangeSubObject(
        workOrderChangeId,
        onSuccess,
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const onSubmitDialog = useCallback(
        (formData) => {
            if (objectToEdit) {
                const input: BimContractSubObjectUpdateInput = {
                    id: objectToEdit.id,
                    name: formData.name,
                    rationale: formData.rationale,
                    hasGuaranteedVolume: true,
                };

                if (workOrderId) {
                    bimUpdateWorkOrderSubObjectMakeRequest(input);
                }

                if (workOrderChangeId) {
                    bimUpdateWorkOrderSubObjectChangeMakeRequest(input);
                }
            } else {
                const input: BimContractSubObjectCreateInput = {
                    contractObjectId: parentObjectToAppend?.id,
                    name: formData.name,
                    rationale: formData.rationale,
                    hasGuaranteedVolume: true,
                };

                if (workOrderId) {
                    bimCreateWorkOrderSubObjectMakeRequest(input);
                }

                if (workOrderChangeId) {
                    bimCreateWorkOrderChangeSubObjectChangeMakeRequest(input);
                    setIsSubmitted(true);
                }
            }
        },
        [
            bimCreateWorkOrderChangeSubObjectChangeMakeRequest,
            bimCreateWorkOrderSubObjectMakeRequest,
            bimUpdateWorkOrderSubObjectChangeMakeRequest,
            bimUpdateWorkOrderSubObjectMakeRequest,
            objectToEdit,
            parentObjectToAppend?.id,
            workOrderChangeId,
            workOrderId,
        ],
    );

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    useEffect(() => {
        if (
            nameValue &&
            bimUniqueContractSubObjectStatus === RequestStatus.SUCCESS &&
            !bimUniqueContractSubObjectData?.result
        ) {
            setError('name', { message: 'Подобъект с таким наименованием уже существует' });
        } else {
            clearErrors('name');
        }
    }, [bimUniqueContractSubObjectData?.result, bimUniqueContractSubObjectStatus, clearErrors, nameValue, setError]);

    useEffect(
        () => () => {
            resetBimUniqueContractSubObjectName(null);
        },
        [resetBimUniqueContractSubObjectName],
    );

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const onCreateReport = () => {
        ExportCSVReport(bimCreateWorkOrderChangeSubObjectData?.bimCreateWorkOrderChangeSubObject.validation);
    };

    return (
        <ModalDialog
            isOnClickOutsideDisable
            title={btnConfirmTitle}
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Style.modal}
            withTitleCloseButton
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                {!objectToEdit && (
                    <Informer
                        view="filled"
                        status="warning"
                        label="Добавление нового подобъекта обновит структуру Договора"
                        className={cnMixSpace({ mB: 'm' })}
                        icon={IconAlert}
                        size="s"
                    />
                )}
                <form>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        control={control}
                        type="text"
                        view="default"
                        labelPosition="top"
                        className={cnMixSpace({ mB: 'm' })}
                        required
                        rules={requiredBaseRule}
                        directionTooltip="upCenter"
                        tooltipSize="l"
                        disabled={!isNameUnconditionallyEnabled && !!objectToEdit && !objectToEdit?.fromWorkOrder}
                    />
                    <ControlledTextField
                        labelPosition="top"
                        name="rationale"
                        label="Обоснование"
                        control={control}
                        type="textarea"
                        cols={50}
                        rows={5}
                        required
                        rules={requiredBaseRule}
                    />
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            <ErrorHandlingModal
                titleText="Создание подобъекта"
                isSubmitted={isSubmitted}
                statusRequest={bimCreateWorkOrderChangeSubObjectStatus}
                dataRequest={bimCreateWorkOrderChangeSubObjectData?.bimCreateWorkOrderChangeSubObject}
                errorsRequest={bimCreateWorkOrderChangeSubObjectError}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </ModalDialog>
    );
};

BimWorkOrderSubObjectEdit.displayName = 'BimWorkOrderSubObjectEdit';
